// Live-region announcements — the "reveal, then write" rule, with a real gap
// between the two.
//
// A live region only announces content that CHANGES while the region is in the
// accessibility tree. Three places in this app had the write and the reveal in
// the wrong order and were fixed (A-01 idle warning, A-02 login error / MFA
// enrolment error); the reorder alone is not enough, because putting both in
// one synchronous block means the browser's next accessibility pass still sees
// the region appear with its text already in place — the same silence, one
// statement later. The fix plans said "on the next tick" for exactly this
// reason and the deferral was the half that did not ship.
//
// A SINGLE requestAnimationFrame is not enough either: a rAF callback runs
// before the accessibility phase of the same frame, so the text can still land
// inside the frame that first exposes the region. Two frames clears it, and a
// ~100 ms timeout clears it on engines with no rAF at all.
//
// HONEST LIMIT: none of this was verified with a real screen reader. What is
// verifiable, and is tested, is that the reveal and the write land in
// different tasks. Whether a given engine then speaks is its own business.

/** Fallback gap where requestAnimationFrame is unavailable (ms). */
export const ANNOUNCE_DELAY_MS = 100;

/**
 * Run `fn` after the engine has had a chance to expose the freshly-revealed
 * region to assistive technology.
 * @param {() => void} fn
 * @returns {void}
 */
export function afterExposure(fn) {
  const w = /** @type {any} */ (globalThis);
  if (w && typeof w.requestAnimationFrame === 'function') {
    w.requestAnimationFrame(() => w.requestAnimationFrame(fn));
    return;
  }
  setTimeout(fn, ANNOUNCE_DELAY_MS);
}

/**
 * Per-element sequence numbers, so a superseded or cancelled announcement
 * never writes over a newer one — a deferred write is a write that can arrive
 * after the region has been hidden or replaced.
 * @type {WeakMap<any, number>}
 */
const seq = new WeakMap();

/**
 * Empty a live region NOW and write its text once the region has been exposed.
 * The caller reveals the region first (class, aria-hidden, whatever that
 * region uses) and then hands it here.
 *
 * Emptying synchronously matters on its own: it is what makes a REPEATED
 * identical message (the second wrong password) a genuine mutation rather than
 * a no-op the screen reader never hears.
 * @param {any} el the live region element
 * @param {string} text
 * @returns {void}
 */
export function announce(el, text) {
  if (!el) return;
  el.textContent = '';
  const gen = (seq.get(el) || 0) + 1;
  seq.set(el, gen);
  afterExposure(() => {
    if (seq.get(el) !== gen) return; // hidden or superseded inside the gap
    el.textContent = text;
  });
}

/**
 * Drop any announcement still pending for a region — call it from whatever
 * hides the region, so a write in flight cannot repopulate a hidden node.
 * @param {any} el
 * @returns {void}
 */
export function cancelAnnounce(el) {
  if (el) seq.set(el, (seq.get(el) || 0) + 1);
}
